import React, { useState, useEffect, useRef } from 'react';
import './PageEditor.css';
import { Box, Paper, Tabs, Tab, IconButton, TextField, Button, Typography, CircularProgress, Divider, Chip } from '@mui/material';
import {
  Code as CodeIcon,
  Visibility as PreviewIcon,
  Chat as ChatIcon,
  ContentCopy as CopyIcon,
  AutoFixHigh as MagicIcon,
} from '@mui/icons-material';

function PageEditor({ initialContent = '', onChange, accountId, microSiteId, pageId }) {
  // Use useRef to track if the component is mounted
  const isMounted = useRef(false);
  
  // Initialize all state with default values
  const [activeView, setActiveView] = useState('code');
  const [showChat, setShowChat] = useState(true);
  const [htmlContent, setHtmlContent] = useState('');
  const [aiMessages, setAiMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [generatingTemplate, setGeneratingTemplate] = useState(false);
  const [editorReady, setEditorReady] = useState(false);
  const [iframeKey, setIframeKey] = useState(0); // Add key for iframe rerendering
  const editorRef = useRef(null);
  const previewRef = useRef(null);
  const containerRef = useRef(null);

  // Handle initial content after mount
  useEffect(() => {
    isMounted.current = true;
    if (initialContent) {
      setHtmlContent(initialContent);
    }
    
    // Set editor as ready after a short delay to ensure DOM is settled
    const timer = setTimeout(() => {
      setEditorReady(true);
    }, 300);
    
    return () => {
      isMounted.current = false;
      clearTimeout(timer);
    };
  }, [initialContent]);

  // Handle content updates
  useEffect(() => {
    if (isMounted.current && initialContent !== htmlContent) {
      setHtmlContent(initialContent);
    }
  }, [initialContent]);

  // Handle ResizeObserver cleanup
  useEffect(() => {
    // Create a more robust handler for ResizeObserver errors
    const handleResizeObserverError = (error) => {
      // Safely check if this is a ResizeObserver error and ignore it
      if (error && (
        (typeof error === 'string' && error.includes('ResizeObserver')) ||
        (error.message && error.message.includes('ResizeObserver'))
      )) {
        // Ignore the ResizeObserver loop limit exceeded error
        return;
      }
      
      // For all other errors, use the original console.error
      console.error(error);
    };
    
    // Save the original console.error
    const originalConsoleError = console.error;
    
    // Replace console.error with our custom handler
    console.error = handleResizeObserverError;
    
    // Cleanup function
    return () => {
      // Restore the original console.error
      console.error = originalConsoleError;
      
      // Disconnect any active ResizeObservers to prevent errors on unmount
      if (window.ResizeObserver && editorRef.current) {
        const observerList = Object.getOwnPropertySymbols(editorRef.current)
          .filter(symbol => String(symbol).includes('ResizeObserver'));
        
        observerList.forEach(symbol => {
          const observer = editorRef.current[symbol];
          if (observer && typeof observer.disconnect === 'function') {
            observer.disconnect();
          }
        });
      }
    };
  }, []);

  // Add global error handler for MutationObserver errors
  useEffect(() => {
    // Create a handler for MutationObserver errors
    const handleMutationObserverError = (event) => {
      if (event && event.message && 
         (event.message.includes('MutationObserver') || 
          event.message.includes('observe') || 
          event.message.includes('Failed to execute') ||
          event.message.includes('not of type'))) {
        // Prevent the error from being displayed in the console
        event.stopImmediatePropagation();
        event.preventDefault();
        return false;
      }
      return true;
    };
    
    // Add event listener for unhandled errors
    window.addEventListener('error', handleMutationObserverError, true);
    
    // Clean up
    return () => {
      window.removeEventListener('error', handleMutationObserverError, true);
    };
  }, []);

  const handleHtmlChange = (e) => {
    const newHtml = e.target.value;
    setHtmlContent(newHtml);
    if (onChange) {
      onChange(newHtml);
    }
    
    // Regenerate the iframe on edit to avoid observer issues
    if (activeView === 'split' || activeView === 'preview') {
      setIframeKey(prev => prev + 1);
    }
  };

  const handleAiChat = async (e) => {
    e.preventDefault();
    if (!userInput.trim() || !isMounted.current) return;

    // Add user message to chat
    const newMessage = { role: 'user', content: userInput };
    setAiMessages(prev => [...prev, newMessage]);
    
    if (isMounted.current) {
      setIsLoading(true);
    }

    try {
      const response = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          message: userInput,
          currentHtml: htmlContent,
          mode: 'html',
          accountId,
          microSiteId,
          pageId
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get AI response');
      }

      const data = await response.json();
      
      if (isMounted.current) {
        // Add AI response to chat
        setAiMessages(prev => [...prev, { role: 'assistant', content: data.message }]);
        
        // If there's suggested HTML, update the editor
        if (data.suggestedHtml) {
          setHtmlContent(data.suggestedHtml);
          onChange?.(data.suggestedHtml);
          // Regenerate the iframe when updating content
          setIframeKey(prev => prev + 1);
        }

        setUserInput('');
      }
    } catch (error) {
      console.error('AI Chat error:', error);
      if (isMounted.current) {
        setAiMessages(prev => [...prev, { 
          role: 'assistant', 
          content: `Sorry, I encountered an error: ${error.message}` 
        }]);
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  };

  const generateHtmlTemplate = async (templateType) => {
    if (!isMounted.current) return;
    
    setGeneratingTemplate(true);
    
    try {
      const response = await fetch('/api/ai/generate-html', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          templateType,
          existingHtml: htmlContent.trim() ? htmlContent : null,
          accountId,
          microSiteId,
          pageId
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate HTML template');
      }

      const data = await response.json();
      
      if (isMounted.current && data.html) {
        setHtmlContent(data.html);
        onChange?.(data.html);
        
        // Regenerate the iframe when updating content
        setIframeKey(prev => prev + 1);
        
        // Add message to the chat
        setAiMessages(prev => [...prev, { 
          role: 'assistant', 
          content: `✨ I've generated a ${templateType} template for you. You can now edit it or ask me to modify specific parts.` 
        }]);
      }
    } catch (error) {
      console.error('Template generation error:', error);
      // Add error message to chat
      setAiMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `Sorry, I couldn't generate the template. Error: ${error.message}` 
      }]);
    } finally {
      if (isMounted.current) {
        setGeneratingTemplate(false);
      }
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(htmlContent);
  };

  // Create a sanitized version of HTML for the iframe
  const getSanitizedHtml = () => {
    try {
      if (!htmlContent) return '';
      
      // If content already has an HTML structure, use it
      if (htmlContent.includes('<html') && htmlContent.includes('</html>')) {
        return htmlContent;
      }
      
      // Otherwise wrap it in a basic HTML structure
      return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta name="mutation-observer-disable" content="true">
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
      margin: 0;
      padding: 20px;
      line-height: 1.6;
    }
    * {
      box-sizing: border-box;
    }
  </style>
</head>
<body>${htmlContent}</body>
</html>`;
    } catch (err) {
      console.warn('Error sanitizing HTML:', err);
      return '';
    }
  };

  // Render with error boundaries
  if (!editorReady) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      height: 'calc(100vh - 124px)', 
      overflow: 'hidden',
      gap: 2 
    }} ref={containerRef} className="editor-container">
      {/* Main Editor Area */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden',
        height: '100%'
      }} className="main-editor-area">
        {/* Toolbar */}
        <Paper sx={{ mb: 1, p: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tabs 
            value={activeView}
            onChange={(e, newValue) => setActiveView(newValue)}
            sx={{ flex: 1 }}
          >
            <Tab icon={<CodeIcon />} label="Code" value="code" />
            <Tab icon={<PreviewIcon />} label="Preview" value="preview" />
            <Tab label="Split View" value="split" />
          </Tabs>
          <IconButton onClick={copyToClipboard} title="Copy HTML">
            <CopyIcon />
          </IconButton>
          <IconButton onClick={() => setShowChat(!showChat)} title="Toggle AI Chat">
            <ChatIcon />
          </IconButton>
        </Paper>

        {/* Editor/Preview Area */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          gap: 2,
          height: 'calc(100% - 60px)',
          overflow: 'hidden',
          ...(activeView === 'split' ? { flexDirection: 'row' } : { flexDirection: 'column' })
        }} className="editor-content-container">
          {(activeView === 'code' || activeView === 'split') && (
            <Paper sx={{ 
              flex: 1, 
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              ...(activeView === 'split' ? { width: '50%' } : { width: '100%' }),
              height: '100%'
            }} className="code-editor-container">
              <TextField
                inputRef={editorRef}
                multiline
                fullWidth
                value={htmlContent}
                onChange={handleHtmlChange}
                variant="outlined"
                className="code-editor"
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  height: '100%',
                  '& .MuiInputBase-root': {
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    height: '100%',
                  },
                  '& .MuiInputBase-inputMultiline': {
                    height: '100% !important',
                    overflowY: 'auto'
                  }
                }}
              />
            </Paper>
          )}

          {(activeView === 'preview' || activeView === 'split') && (
            <Paper 
              sx={{ 
                flex: 1, 
                overflow: 'hidden',
                ...(activeView === 'split' ? { width: '50%' } : { width: '100%' }),
                height: '100%'
              }}
              ref={previewRef}
              className="preview-container"
            >
              <div className="iframe-container" style={{ width: '100%', height: '100%' }}>
                {/* Use key to force iframe recreation when content changes */}
                <iframe 
                  key={iframeKey}
                  title="HTML Preview"
                  srcDoc={getSanitizedHtml()}
                  className="preview-frame"
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    border: 'none',
                    backgroundColor: '#ffffff'
                  }}
                  sandbox="allow-same-origin"
                />
              </div>
            </Paper>
          )}
        </Box>
      </Box>

      {/* AI Chat Panel */}
      {showChat && (
        <Paper sx={{ 
          width: 300, 
          display: 'flex', 
          flexDirection: 'column', 
          height: '100%',
          overflow: 'hidden'
        }} className="chat-container">
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">AI Assistant</Typography>
          </Box>
          
          {/* Template Generator Section */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" gutterBottom>
              Generate HTML Template
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => generateHtmlTemplate('landing')}
                disabled={generatingTemplate}
                startIcon={generatingTemplate ? <CircularProgress size={16} /> : <MagicIcon />}
              >
                Landing
              </Button>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => generateHtmlTemplate('product')}
                disabled={generatingTemplate}
                startIcon={generatingTemplate ? <CircularProgress size={16} /> : <MagicIcon />}
              >
                Product
              </Button>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => generateHtmlTemplate('portfolio')}
                disabled={generatingTemplate}
                startIcon={generatingTemplate ? <CircularProgress size={16} /> : <MagicIcon />}
              >
                Portfolio
              </Button>
            </Box>
          </Box>
          
          <Box sx={{ 
            flex: 1, 
            overflow: 'auto', 
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            height: 'calc(100% - 170px)'
          }} className="chat-messages">
            {aiMessages.length === 0 ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                color: 'text.secondary',
                p: 3,
                textAlign: 'center'
              }}>
                <Typography variant="body2">
                  Hello! I can help you create and edit HTML/CSS. Ask me anything or try one of the template generators above.
                </Typography>
              </Box>
            ) : (
              aiMessages.map((msg, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    bgcolor: msg.role === 'assistant' ? 'grey.100' : 'primary.light',
                    color: msg.role === 'assistant' ? 'text.primary' : 'white',
                    alignSelf: msg.role === 'assistant' ? 'flex-start' : 'flex-end',
                    maxWidth: '80%',
                  }}
                >
                  <Typography variant="body2">{msg.content}</Typography>
                </Box>
              ))
            )}
          </Box>

          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            <form onSubmit={handleAiChat} style={{ display: 'flex', gap: 8 }}>
              <TextField
                size="small"
                fullWidth
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                placeholder="Ask AI for help with HTML/CSS..."
                disabled={isLoading}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading}
                sx={{ minWidth: 100 }}
              >
                {isLoading ? 'Thinking...' : 'Send'}
              </Button>
            </form>
          </Box>
        </Paper>
      )}
    </Box>
  );
}

export default PageEditor; 